/*
 * Alpine + Plugins
 *   - mask : https://alpinejs.dev/plugins/mask
 *   - intersect : https://alpinejs.dev/plugins/intersect
 * Input, Controls, Directives
 *   - choices.js : https://choices-js.github.io/Choices/
 *   - tippy : https://atomiks.github.io/tippyjs/
 *   - swipper: https://swiperjs.com/
 *   - intl-tel-input: https://github.com/jackocnr/intl-tel-input
 * Performance, Responsivness
 *   - lazysizes : https://afarkas.github.io/lazysizes/index.html
 * Animations
 *   - search-animation: custom
 * 3rd party services
 *   - LiveChatWidget: https://platform.text.com/docs/extending-chat-widget/javascript-api
 *   - Broadcast (pusher): https://pusher.com/docs/channels/getting_started/javascript/
 * Services (Alpine.magic)
 *   - Timer (debounce)
 *   - Withdrawal
 */

"use strict";

/* ================================ */
/* Imports
/* ================================ */

/* (Alpine + Plugins)
   ----------------------------- */
import Alpine from "alpinejs";
import mask from "@alpinejs/mask";
import intersect from "@alpinejs/intersect";
import { useStores } from "./services/stores";

/* (Input, Controls, Directives)
   ----------------------------- */
import Choices from "choices.js";
import "choices.js/public/assets/styles/choices.min.css";
import tippy from "tippy.js";
import "tippy.js/dist/tippy.css";
import Swiper from "swiper";
import "swiper/scss";
import "swiper/scss/pagination";
import "swiper/scss/scrollbar";
import { Autoplay, Navigation, Pagination, Scrollbar } from "swiper/modules";
import { fitText } from "./services/fitText";
import { dragToScroll } from "./services/dragToScroll";
import { useIntlTelInput } from "./services/intl-tel-input";
import { useLanguageSelector } from "./services/language-selector";
import { useDirectModals } from "./services/direct-modals";
import { useTimer } from "./services/timer";
import { useAutoScroll } from "./services/autoscroll";
import { useTriggerEvent } from "./services/triggerEvent";
import { useVideo } from "./services/video";

/* (Performance, Responsivness)
   ----------------------------- */
import "lazysizes";

/* (Animations)
   ----------------------------- */
import { useSearchAnimation } from "./animations/search";
import { useWebGL } from "./services/webgl";

/* (3rd party services)
   ----------------------------- */
import { useLivechat } from "./services/livechat";
import { useBroadcast } from "./services/broadcast";
import { useSportsBook } from "./services/sportsbook";
import { useSportsBookV2 } from "./services/sportsbookV2";
import { usePaymentRequest } from "./services/paymentRequest";
import { useCryptoCurrencies } from "./services/cryptocurrency";
import { useDepositBonus } from "./services/depositBonus";
import { useAutofocus } from "./services/autofocus";
import { useSupport } from "./services/support";
import { initModalScrollLock } from "./services/modalScrollLock";

/* ================================ */
/* Register
/* ================================ */

// add alpine plugins
Alpine.plugin(mask);
Alpine.plugin(intersect);

// get composables
const liveChat = useLivechat();
const intlTelInput = useIntlTelInput();
const languageSelector = useLanguageSelector();
const broadcast = useBroadcast();
const stores = useStores();
const directModals = useDirectModals();
const timer = useTimer();
const triggerEvent = useTriggerEvent();
const video = useVideo();

/* ================================ */
/* Event Handler
/* ================================ */

document.addEventListener("alpine:init", () => {
    window.Alpine.magic("debounce", () => {
        return (fn, time = 300) => timer.debounce(fn, time);
    });
    window.Alpine.magic("autofocus", useAutofocus);
    window.Alpine.magic("withdrawal", usePaymentRequest);
    window.Alpine.magic("deposit", () => ({
        payment: usePaymentRequest(),
        bonus: useDepositBonus(),
    }));
    window.Alpine.magic("cryptoCurrencies", useCryptoCurrencies);
    window.Alpine.magic("sportsbook", useSportsBook);
    window.Alpine.magic("sportsbookV2", useSportsBookV2);
    window.Alpine.magic("webgl", useWebGL);
    window.Alpine.magic("autoScroll", useAutoScroll);

    // Directives
    window.Alpine.directive("fit-text", fitText);
    window.Alpine.directive("drag-to-scroll", dragToScroll);
    // Support actions magic: exposes methods from useSupport composable
    window.Alpine.magic("support", useSupport);
});

document.addEventListener("livewire:init", () => {
    // expose tools, utils, etc. through the global window instance.
    window.Choices = Choices;
    window.Tippy = tippy;
    window.IntlTelInput = intlTelInput;
    window.Broadcast = broadcast;
    window.LanguageSelectorCallbackOnCreateTemplates =
        languageSelector.callbackOnCreateTemplates;

    window.animations = {
        ...(window.animations ?? {}),
        ...{ search: useSearchAnimation },
    };

    window.Swiper = Swiper.use([Autoplay, Navigation, Pagination, Scrollbar]);

    // <!> `alpine:init` handler for stores MUST be call after `livewire:init`, otherwise stores will not be available in blades.
    document.addEventListener("alpine:init", stores.init);
});

let isAppLoaded = false;
const onAppLoaded = (timeout = 150) => {
    if (!isAppLoaded) {
        // avoid dispatch if already done.
        // Dispatch a custom event named 'loaded' to signal that the page has fully loaded
        window.dispatchEvent(
            new CustomEvent("loaded", {
                detail: { timeout },
            })
        );
        isAppLoaded = true;
    }
};

document.addEventListener("DOMContentLoaded", () => {
    // clear sessions store
    window.Alpine.store("sessions").clear();
    // reset provider filters when page changes.
    window.Alpine.store("filters").games.providers.clear();
    // get the channels list to connect to
    const channels = ["private", "presence"];
    const path = window.location.pathname;
    if (/^\/casino\/[^/]+\/[^/]+$/.exec(path)) {
        channels.push("sessions");
    }
    // initializer Pusher/Echo channels
    broadcast.init(channels);
    // initialize liveChat widget if enabled
    if (window?.appContext?.Config?.livechat?.enabled) {
        liveChat.init();
    }

    if (path == "/") {
        const spinePlayerElement = document.querySelector(".spine-player");
        const videos = Array.from(document.querySelectorAll("video"));

        switch (true) {
            // spine-player
            case !!spinePlayerElement: {
                const onLoaded = () => {
                    onAppLoaded(0); // no extra timeout, animation are ready to play.
                    window.removeEventListener("HomeBannerLoaded", onLoaded);
                };
                window.addEventListener("HomeBannerLoaded", onLoaded);
                break;
            }

            // at least one video:
            case videos.length > 0: {
                video.canplay().then(() => {
                    video.autoplay();
                    onAppLoaded(0); // no extra timeout, video are ready to play.
                });
                break;
            }

            // default
            default:
                onAppLoaded(); // default timeout, for images to load
        }
    } else {
        // if not HomePage consider the page already enough loaded / ready.
        onAppLoaded(); // default timeout, for images to load
    }

    // detect if requested to open a modal
    directModals.detect();
    // prevent layout shift when opening modals (reserve scrollbar width)
    initModalScrollLock();
});

window.addEventListener("load", () => {
    // When load event is received.
    onAppLoaded(0);
});

window.addEventListener("message", (event) => {
    // Update livechat data from iframe error.
    liveChat.onMessage(event);
});

// Listen for Livewire tracking events
Livewire.on("trackingEvent", (event) => {
    triggerEvent.on(event);
});

window.triggerEvent = triggerEvent;
